import { useSyncExternalStore } from 'react';

/**
 * Splash → screen handoff store (KAN-562, onboarding round 5).
 *
 * SplashOverlay publishes WHERE the splash is in its life, so a screen mounted
 * underneath it can choreograph its own entrance off the splash's exit
 * instead of guessing with timers:
 *   - 'covered' — the splash is up (opaque) over the app;
 *   - 'leaving' — the splash has started to go (the wordmark glide, if any,
 *                 starts here — this is t0 of every welcome timeline);
 *   - 'clear'   — the splash is gone (its fade finished), or was never up.
 *
 * Default is 'clear': a tree rendered without SplashOverlay (unit tests, a
 * screen opened later in the session) behaves exactly as before this store.
 *
 * Two more one-shot facts ride here:
 *   - `expectSplashGlide()` — a screen that DECIDED to show a glide target but
 *     can mount it only once the splash starts leaving asks the overlay to
 *     wait a few frames for that target before it fades (bounded:
 *     SPLASH_GLIDE_TARGET_WAIT_MS). Both welcomes today mount their target
 *     UNDER the splash instead, so nothing has to be waited for.
 *   - the landed target — the glide reports the element it landed on, so that
 *     target can swap its real wordmark in on the exact frame the clone leaves.
 *     Cleared at 'clear' and at sign-out (`clearSplashGlideLanded`), so the
 *     store never retains a detached node.
 *   - whether the overlay is still WAITING for a target (`awaitingTarget`): a
 *     target that mounts after that window gets no glide, so it must never
 *     arm its hide (useSplashGlideTarget).
 *
 * Module-level on purpose (like signInCover / arrivalModalMutex): the overlay
 * is a singleton above every session-keyed tree. Holds no tenant data.
 */

export const SPLASH_PHASE = Object.freeze({ COVERED: 'covered', LEAVING: 'leaving', CLEAR: 'clear' });

/** How long the overlay waits for an expected glide target before fading anyway. */
export const SPLASH_GLIDE_TARGET_WAIT_MS = 200;

let phase = SPLASH_PHASE.CLEAR;
let leaveSeq = 0;
let expected = 0;
let landed = null; // { el, seq }
let awaitingTarget = false;
let glideNear = false; // the glide is ~done (or none runs): a screen may start its own entrance
let veil = false; // the quiet sign-out veil is the cover: it carries no wordmark, so nothing may glide or hide for it
const listeners = new Set();

function emit() {
  listeners.forEach((l) => { try { l(); } catch (_e) { /* never break the others */ } });
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSplashPhase() {
  return phase;
}

/** Monotonic id of the current/last leave — lets a timeline start once per leave. */
export function getSplashLeaveSeq() {
  return leaveSeq;
}

/** SplashOverlay only. */
export function setSplashPhase(next) {
  if (next === phase) return;
  if (next === SPLASH_PHASE.LEAVING) leaveSeq += 1;
  // The splash (and any glide — it lands no later than 'clear') is gone: drop
  // the landed element so the store never holds a node that may detach.
  if (next !== SPLASH_PHASE.LEAVING) { landed = null; awaitingTarget = false; }
  if (next !== SPLASH_PHASE.LEAVING) glideNear = false;
  if (next === SPLASH_PHASE.CLEAR) veil = false;
  phase = next;
  emit();
}

export function useSplashPhase() {
  return useSyncExternalStore(subscribe, getSplashPhase, getSplashPhase);
}

const isClear = () => phase === SPLASH_PHASE.CLEAR;

/** True once the splash is gone (or was never up) — re-renders only when that flips. */
export function useSplashClear() {
  return useSyncExternalStore(subscribe, isClear, isClear);
}

/**
 * Ask the overlay to wait (bounded) for a glide target that will mount as the
 * splash starts leaving. Returns a release; idempotent.
 */
export function expectSplashGlide() {
  expected += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    expected = Math.max(0, expected - 1);
  };
}

export function isSplashGlideExpected() {
  return expected > 0;
}

/** SplashOverlay only: the cover currently up is the quiet veil (no wordmark, so no glide target may arm). */
export function setSplashVeil(next) {
  veil = Boolean(next);
}

/** SplashOverlay only: it is (still) looking for a glide target to fly onto. */
export function setSplashAwaitingGlideTarget(next) {
  awaitingTarget = Boolean(next);
}

/** True only while a leaving splash has not yet launched (or skipped) its glide. */
export function isSplashAwaitingGlideTarget() {
  return !veil && (phase === SPLASH_PHASE.COVERED || (phase === SPLASH_PHASE.LEAVING && awaitingTarget));
}

/** splashGlide only: the clone landed on `el` (or the glide was skipped). */
export function notifySplashGlideLanded(el) {
  landed = { el, seq: leaveSeq };
  emit();
}

/**
 * The glide is >= SPLASH_GLIDE_NEAR_FRACTION done, or no glide will run: the
 * screen under it may play its entrance now so it arrives as the logo settles.
 * Reset when the phase leaves 'leaving'.
 */
export const SPLASH_GLIDE_NEAR_FRACTION = 0.88;
export function notifySplashGlideNear() {
  if (glideNear) return;
  glideNear = true;
  emit();
}
export function getSplashGlideNear() {
  return glideNear;
}
export function useSplashGlideNear() {
  return useSyncExternalStore(subscribe, getSplashGlideNear, getSplashGlideNear);
}

/** Sign-out (clearPerUserDeviceState / clearSignedOutMemoryState, via resetSplashGlideSession). */
export function clearSplashGlideLanded() {
  if (landed == null) return;
  landed = null;
  emit();
}

export function getSplashGlideLanded() {
  return landed;
}

export function useSplashGlideLanded() {
  return useSyncExternalStore(subscribe, getSplashGlideLanded, getSplashGlideLanded);
}

/** @internal tests only */
export function __resetSplashHandoffForTests() {
  phase = SPLASH_PHASE.CLEAR;
  leaveSeq = 0;
  expected = 0;
  landed = null;
  awaitingTarget = false;
  glideNear = false;
  veil = false;
  listeners.clear();
}
